import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { X, Users, Loader2, Search, Upload, Trash2, Plus } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const EmailListMembersModal = ({ open, onClose, list, onChange }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (open && list) {
      loadDetail();
      setSearchParam("");
      setSearchResults([]);
    }
  }, [open, list]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/email-marketing/lists/${list.id}`);
      setMembers(data.contacts || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleSearch = async (value) => {
    setSearchParam(value);
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get("/contacts", {
        params: { searchParam: value },
      });
      const memberIds = new Set(members.map((m) => m.id));
      setSearchResults((data.contacts || []).filter((c) => !memberIds.has(c.id)));
    } catch (err) {
      toastError(err);
    }
    setSearching(false);
  };

  const handleAddContact = async (contact) => {
    try {
      await api.post(`/email-marketing/lists/${list.id}/contacts`, {
        contactIds: [contact.id],
      });
      setSearchResults((prev) => prev.filter((c) => c.id !== contact.id));
      loadDetail();
      if (onChange) onChange();
    } catch (err) {
      toastError(err);
    }
  };

  const handleRemoveContact = async (contactId) => {
    try {
      await api.delete(
        `/email-marketing/lists/${list.id}/contacts/${contactId}`
      );
      loadDetail();
      if (onChange) onChange();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setImporting(true);
    try {
      const { data } = await api.post(
        `/email-marketing/lists/${list.id}/import`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(
        `${data.imported} contacto(s) importado(s)${
          data.skipped > 0 ? `, ${data.skipped} fila(s) omitida(s)` : ""
        }`
      );
      loadDetail();
      if (onChange) onChange();
    } catch (err) {
      toastError(err);
    }
    setImporting(false);
    e.target.value = "";
  };

  if (!open || !list) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Users className="w-4 h-4 text-indigo-600" />
            Miembros de "{list.name}"
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchParam}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Buscar contacto por nombre o número para agregar..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            />
          </div>

          {searching && (
            <div className="flex justify-center py-2">
              <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="max-h-32 overflow-y-auto space-y-1 border border-slate-100 dark:border-slate-800 rounded-lg p-1">
              {searchResults.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => handleAddContact(contact)}
                  className="w-full flex items-center justify-between px-2 py-1.5 text-sm rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                >
                  <span className="truncate">
                    {contact.name}{" "}
                    <span className="text-slate-400">
                      {contact.email || contact.number}
                    </span>
                  </span>
                  <Plus className="w-3.5 h-3.5 text-green-600 shrink-0" />
                </button>
              ))}
            </div>
          )}

          <label className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium border border-dashed border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer text-slate-600 dark:text-slate-300">
            {importing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Importar CSV (email,nombre — una fila por línea)
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={handleCsvUpload}
              disabled={importing}
              className="hidden"
            />
          </label>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">
              Esta lista todavía no tiene contactos.
            </p>
          ) : (
            <div className="space-y-1">
              {members.map((contact) => (
                <div
                  key={contact.id}
                  className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-slate-900 dark:text-slate-100 truncate">
                      {contact.name}
                    </p>
                    <p className="text-xs text-slate-400 truncate">
                      {contact.email || "sin correo"}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveContact(contact.id)}
                    title="Quitar de la lista"
                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 shrink-0">
          <p className="text-xs text-slate-400">
            {members.length} contacto{members.length !== 1 && "s"} en la lista
            {members.filter((c) => !c.email).length > 0 &&
              ` · ${members.filter((c) => !c.email).length} sin correo (no recibirán campañas)`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailListMembersModal;
